// Bykortet: et tryk på en by eller et datacenter viser, hvordan den har det, og hvad der hjælper
// (IGN runde 1: "jeg kunne ikke se, hvad byen manglede, før den gik i sort"). Kortet står over byen,
// følger kameraet og tager aldrig imod tryk, så næste tryk på målet stadig bygger.
//
// Tal i procent og sekunder, aldrig MW (DESIGN.md: tekst og tal). Rød bruges kun til mørkeuret.

import { adviceText, coachModeOf } from '../ai/coach';
import { bottleneck, bottleneckText, truthForCity } from '../ai/truth';
import { darkInTicks, isPowered, TICK_HZ } from '../sim';
import type { GridNode, LevelDef, PlayerId, State } from '../sim';
import { announce } from './a11y';
import { h, setText } from './dom';

export type CardTone = 'ok' | 'warn' | 'dark' | 'calm';

export interface NodeCardInfo {
  title: string;
  /** "Datacenter" eller tom. */
  sub: string;
  /** Hovedlinjen: hvor meget strøm byen får. */
  status: string;
  tone: CardTone;
  /** Veje ind: "Én vej ind", "To veje ind" ... */
  ways: string;
  /** Mørkeuret eller fristen, tom når intet løber. */
  clock: string;
  /** Hvorfor byen mangler strøm, præcist: "Ledningen Skive-Viborg er fuld". Tom, når den får nok. */
  why: string;
  /** Trænerens forslag for netop denne by, tom når der ikke er et (eller træneren er slået fra). */
  tip: string;
}

/** Hvem der kigger: spillerens plads og makkerens navn, når trækket er makkerens job. */
export interface CardViewer {
  player: PlayerId;
  /** "Volt", "Driftsvagten" eller "Planlæggeren". */
  partner: string;
}

const capital = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

const WAYS = ['Ingen vej ind', 'Én vej ind', 'To veje ind', 'Tre veje ind'];

function waysIn(state: State, id: string): number {
  return state.lines.filter((l) => l.status === 'ok' && (l.a === id || l.b === id)).length;
}

/** Bykortets indhold som en ren funktion af state (testes i test/ui/nodecard.test.ts). */
export function nodeCardInfo(state: State, level: LevelDef, id: string, touch: boolean, viewer: CardViewer = { player: 'p1', partner: 'Makker' }): NodeCardInfo | null {
  const n: GridNode | undefined = state.nodes.find((x) => x.id === id);
  const c = n?.city;
  if (!n || !c || !n.active || n.phantom) return null;
  const k = waysIn(state, id);
  const info: NodeCardInfo = {
    title: n.name,
    sub: n.kind === 'datacenter' ? 'Datacenter' : '',
    status: '',
    tone: 'ok',
    ways: WAYS[k] ?? `${k} veje ind`,
    clock: '',
    why: '',
    tip: '',
  };
  const pct = c.demandMw > 0 ? Math.min(100, Math.floor((c.servedMw * 100) / c.demandMw)) : 100;
  const anyLine = state.lines.some((l) => l.a === id || l.b === id);
  if (c.darkTicks > 0) {
    info.status = 'Mørk lige nu';
    info.tone = 'dark';
    info.clock = `Tænder tidligst om ${Math.ceil(c.darkTicks / TICK_HZ)} s`;
  } else if (!anyLine) {
    info.status = 'Ingen ledning endnu';
    info.tone = 'warn';
  } else if (isPowered(n)) {
    info.status = pct >= 100 ? 'Får al sin strøm' : `Får ${pct} % af sin strøm`;
  } else {
    // Rundet ned, så en by i nød aldrig ser ud til at få 90 % eller mere.
    info.status = c.out ? `Slukket. Får ${Math.min(89, pct)} %` : `Får ${Math.min(89, pct)} %, mangler ${100 - Math.min(89, pct)} %`;
    info.tone = 'warn';
  }
  // IGN runde 2: "Sort om 12 s" ved siden af "tænder om et øjeblik". Er strømmen på vej, er der intet ur.
  // Spor "sandhed": samme træk som hintet og Volt (src/ai/truth.ts), og flaskehalsen sagt præcist.
  const a = truthForCity(state, id, viewer.player);
  const soon = a?.kind === 'calm' && a.soon === true;
  if (c.darkTicks === 0) {
    const left = soon ? null : darkInTicks(state, level, n);
    if (soon) {
      info.clock = 'Tænder om et øjeblik';
      info.tone = 'calm';
    } else if (left !== null) {
      info.clock = `Sort om ${Math.max(1, Math.ceil(left / TICK_HZ))} s`;
      info.tone = 'dark';
    } else if (!isPowered(n) && c.graceTicks > 0) {
      info.clock = `Uret starter om ${Math.ceil(c.graceTicks / TICK_HZ)} s`;
    } else if (!isPowered(n) && state.year < level.clockFrozenUntilYear) {
      info.clock = 'Uret står stille i år';
      info.tone = 'calm';
    }
  }
  if (!soon && anyLine && c.darkTicks === 0) {
    const b = bottleneck(state, id);
    if (b.kind !== 'ok' && b.kind !== 'noline') info.why = `${capital(bottleneckText(b))}.`;
  }
  if (coachModeOf(state) !== 'off' && !soon) {
    if (a && a.kind !== 'praise') {
      // Første sætning gentager kortets procent ("Esbjerg får kun 80 % af sin strøm."): den ryger.
      const tip = adviceText(state, a, touch).replace(/^.+? får kun \d+ % af sin strøm\. /, '');
      info.tip = a.partner ? `${viewer.partner}s job: ${tip}` : tip;
    }
  }
  return info;
}

export class NodeCard {
  readonly el: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly subEl: HTMLElement;
  private readonly statusEl: HTMLElement;
  private readonly waysEl: HTMLElement;
  private readonly clockEl: HTMLElement;
  private readonly whyEl: HTMLElement;
  private readonly tipEl: HTMLElement;
  private id: string | null = null;
  private nextAt = 0;
  private lastAnnounced = '';

  constructor() {
    this.titleEl = h('strong', { class: 'nodecard-title' });
    this.subEl = h('span', { class: 'nodecard-sub' });
    this.statusEl = h('span', { class: 'nodecard-status' });
    this.waysEl = h('span', { class: 'nodecard-ways' });
    this.clockEl = h('span', { class: 'nodecard-clock' });
    this.whyEl = h('span', { class: 'nodecard-why' });
    this.tipEl = h('p', { class: 'nodecard-tip' });
    this.el = h(
      'div',
      { class: 'nodecard', hidden: true, 'aria-hidden': 'true' },
      h('div', { class: 'nodecard-head' }, this.titleEl, this.subEl),
      h('div', { class: 'nodecard-row' }, this.statusEl, this.waysEl),
      this.clockEl,
      this.whyEl,
      this.tipEl,
    );
  }

  get shown(): string | null {
    return this.id;
  }

  show(id: string): void {
    this.id = id;
    this.nextAt = 0;
    this.lastAnnounced = '';
  }

  hide(): void {
    this.id = null;
    this.el.hidden = true;
  }

  /**
   * Hver frame: følg byen på skærmen og opdatér teksten et par gange i sekundet. `at` er byens punkt
   * på lærredet; `area` det frie felt, kortet holdes inden for.
   */
  update(state: State, level: LevelDef, at: { x: number; y: number } | null, k: number, area: { x: number; y: number; w: number; h: number }, touch: boolean, viewer?: CardViewer): void {
    if (!this.id) return;
    if (!at) {
      this.hide();
      return;
    }
    const now = performance.now();
    if (now >= this.nextAt) {
      this.nextAt = now + 250;
      const info = nodeCardInfo(state, level, this.id, touch, viewer);
      if (!info) {
        this.hide();
        return;
      }
      setText(this.titleEl, info.title);
      setText(this.subEl, info.sub);
      this.subEl.hidden = !info.sub;
      setText(this.statusEl, info.status);
      setText(this.waysEl, info.ways);
      setText(this.clockEl, info.clock);
      this.clockEl.hidden = !info.clock;
      setText(this.whyEl, info.why);
      this.whyEl.hidden = !info.why;
      setText(this.tipEl, info.tip);
      this.tipEl.hidden = !info.tip;
      this.el.dataset.tone = info.tone;
      // Skærmlæseren får det første billede af byen, ikke hvert sekund på uret.
      const said = `${info.title}. ${info.status}. ${info.ways}.`;
      if (said !== this.lastAnnounced) {
        this.lastAnnounced = said;
        announce(`${said}${info.clock ? ` ${info.clock}.` : ''}${info.why ? ` ${info.why}` : ''}${info.tip ? ` ${info.tip}` : ''}`);
      }
    }
    this.el.hidden = false;
    // Over byen; er der ikke plads, under den. Altid inden for det frie felt.
    const w = this.el.offsetWidth;
    const hgt = this.el.offsetHeight;
    const gap = 54 * k;
    let y = at.y - gap - hgt;
    if (y < area.y + 4) y = at.y + 26 * k;
    y = Math.max(area.y + 4, Math.min(area.y + area.h - hgt - 4, y));
    const x = Math.max(area.x + 4, Math.min(area.x + area.w - w - 4, at.x - w / 2));
    this.el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }
}
